'use client'

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox'
import { cn } from '@/lib/utils'

export interface ÅSelectOption {
  value: string
  label: string
}

interface ÅSelectCommonProps {
  items: ÅSelectOption[]
  id?: string
  name?: string
  placeholder?: string
  emptyLabel?: string
  required?: boolean
  disabled?: boolean
  clearable?: boolean
  className?: string
  'aria-label'?: string
}

export interface ÅSelectSingleProps extends ÅSelectCommonProps {
  multiple?: false
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
}

export interface ÅSelectMultipleProps extends ÅSelectCommonProps {
  multiple: true
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
}

export type ÅSelectProps = ÅSelectSingleProps | ÅSelectMultipleProps

interface SelectOptionsProps {
  emptyLabel: string
  anchor?: React.RefObject<HTMLDivElement | null>
}

function SelectOptions({ emptyLabel, anchor }: SelectOptionsProps) {
  return (
    <ComboboxContent anchor={anchor}>
      <ComboboxEmpty>{emptyLabel}</ComboboxEmpty>
      <ComboboxList>
        {(item: ÅSelectOption) => (
          <ComboboxItem key={item.value} value={item}>
            {item.label}
          </ComboboxItem>
        )}
      </ComboboxList>
    </ComboboxContent>
  )
}

function MultipleSelect(props: ÅSelectMultipleProps) {
  const {
    items,
    value,
    defaultValue,
    onValueChange,
    id,
    name,
    placeholder = '请选择',
    emptyLabel = '无匹配项',
    required,
    disabled,
    className,
    'aria-label': ariaLabel,
  } = props
  const anchor = useComboboxAnchor()
  const findAll = (targets?: string[]) =>
    targets?.flatMap((target) => {
      const item = items.find((candidate) => candidate.value === target)
      return item ? [item] : []
    }) ?? []
  const isControlled = value !== undefined

  return (
    <Combobox
      multiple
      items={items}
      name={name}
      required={required}
      disabled={disabled}
      value={isControlled ? findAll(value) : undefined}
      defaultValue={isControlled ? undefined : findAll(defaultValue)}
      onValueChange={(selected) =>
        onValueChange?.(selected.map((item) => item.value))
      }
      isItemEqualToValue={(a, b) => a.value === b.value}
    >
      <ComboboxChips
        ref={anchor}
        className={cn('w-fit min-w-32 max-w-full', className)}
      >
        <ComboboxValue>
          {(selected: ÅSelectOption[]) => (
            <>
              {selected.map((item) => (
                <ComboboxChip key={item.value} className="max-w-full">
                  <span className="truncate">{item.label}</span>
                </ComboboxChip>
              ))}
            </>
          )}
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          aria-label={ariaLabel}
          placeholder={value?.length ? undefined : placeholder}
          disabled={disabled}
        />
      </ComboboxChips>
      <SelectOptions emptyLabel={emptyLabel} anchor={anchor} />
    </Combobox>
  )
}

function SingleSelect(props: ÅSelectSingleProps) {
  const {
    items,
    value,
    defaultValue,
    onValueChange,
    id,
    name,
    placeholder = '请选择',
    emptyLabel = '无匹配项',
    required,
    disabled,
    clearable,
    className,
    'aria-label': ariaLabel,
  } = props
  const find = (target?: string) =>
    items.find((item) => item.value === target) ?? null
  const isControlled = value !== undefined

  return (
    <Combobox
      items={items}
      name={name}
      required={required}
      disabled={disabled}
      value={isControlled ? find(value) : undefined}
      defaultValue={isControlled ? undefined : find(defaultValue)}
      onValueChange={(item) => onValueChange?.(item?.value ?? '')}
      isItemEqualToValue={(a, b) => a?.value === b?.value}
    >
      <ComboboxInput
        id={id}
        aria-label={ariaLabel}
        placeholder={placeholder}
        disabled={disabled}
        showClear={clearable}
        className={cn(
          'w-fit min-w-24 [&>input]:field-sizing-content',
          className,
        )}
      />
      <SelectOptions emptyLabel={emptyLabel} />
    </Combobox>
  )
}

function AppSelect(props: ÅSelectProps) {
  return props.multiple ? (
    <MultipleSelect {...props} />
  ) : (
    <SingleSelect {...props} />
  )
}

export { AppSelect as ÅSelect }
